import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useUnreadCounts } from '@/hooks/useUnreadCounts';
import { UnreadBadge } from './sidebar-primitives';

/**
 * The bell is the Unreads badge (ui-ux-guideline §2.7, §2.8 rule 4;
 * DECISIONS 2026-09-18 "Subtraction in the chrome", Q3).
 *
 * In the TRFer shell the app-bar bell shows the same number as the Unreads
 * sidebar row — `TrferUnreadCounts.threads`, from the same polled endpoint,
 * in the same `UnreadBadge` chip — and pressing it opens Unreads. One
 * number, one place: the old bell counted notification rows nobody tapped
 * (one per case manager per handoff, uncleared by claiming) while the
 * sidebar counted open handoffs, so the two could never agree by tuning.
 *
 * A link, not a popover: there is nothing to peek at. Claiming is the open.
 * The notification popover (`NotificationPanel`) stays for parents and
 * admins.
 *
 * Accessible name: "Unreads" at zero, "Unreads, 3 unread" otherwise — on the
 * link itself, with the badge hidden from the tree so the number is not
 * read twice.
 */
export function UnreadsBell() {
  const { counts } = useUnreadCounts();
  const { t } = useTranslation('shell');
  const count = counts.threads;
  const name = count > 0 ? t('unreadsBell.nameWithCount', { count }) : t('unreadsBell.name');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to="/trfer/threads"
          aria-label={name}
          className="relative inline-flex h-11 w-11 items-center justify-center rounded-md text-stevens-ink-2 transition-colors hover:bg-stevens-bg-2 hover:text-stevens-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          <span aria-hidden="true" className="absolute -top-0.5 -right-0.5">
            <UnreadBadge count={count} />
          </span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom">{t('unreadsBell.name')}</TooltipContent>
    </Tooltip>
  );
}
